import Bugsnag from "@bugsnag/js";
import { Centrifuge } from "centrifuge";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  clearNewChatUnreadRoomIds,
  fetchUnreadChatRoomIds,
  removeNewChatUnreadRoomId,
  upsertNewChatUnreadRoomId,
} from "../Store/Actions/chatActions";
import { updateClientChatRoomUnreadCount } from "../Store/Actions/clientActions";
import axiosInstance from "../axiosInstance";
import { apiURL, centrifugoUrl } from "../variable";

const getConnectionToken = async () => {
  const response = await axiosInstance.post(`${apiURL}/chat/centrifugo`);
  return response?.data?.data?.token;
};

const useNewChatUnreadListener = () => {
  const dispatch = useDispatch();
  const userId = useSelector(state => state.auth?.user?.id);
  const isNewChatEnabled = useSelector(
    state => !!state.provider?.providerProfile?.new_chat_enabled
  );

  const clientRef = useRef(null);
  const subscriptionRef = useRef(null);

  useEffect(() => {
    if (!userId || !isNewChatEnabled) {
      dispatch(clearNewChatUnreadRoomIds());

      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }

      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }

      return;
    }

    dispatch(fetchUnreadChatRoomIds());

    const client = new Centrifuge(centrifugoUrl, {
      getToken: getConnectionToken,
    });
    const subscription = client.newSubscription(`chat:inbox:${userId}`);

    const syncUnreadRoom = data => {
      if (!data?.room_id) return;

      if (data.unread_count > 0) {
        dispatch(upsertNewChatUnreadRoomId(data.room_id));
      } else {
        dispatch(removeNewChatUnreadRoomId(data.room_id));
      }

      dispatch(
        updateClientChatRoomUnreadCount(data.room_id, data.unread_count)
      );
    };

    const handlePublication = publication => {
      const { event, data } = publication?.data || {};

      if (event === "room.updated") {
        syncUnreadRoom(data);
      }
    };

    const handleSubscribed = ctx => {
      if (ctx?.wasRecovering && !ctx?.recovered) {
        dispatch(fetchUnreadChatRoomIds());
      }
    };

    const handleError = error => {
      Bugsnag.notify(error?.error || error);
    };

    client.on("error", handleError);
    subscription.on("publication", handlePublication);
    subscription.on("subscribed", handleSubscribed);

    subscription.subscribe();
    client.connect();

    clientRef.current = client;
    subscriptionRef.current = subscription;

    return () => {
      subscription.removeListener("publication", handlePublication);
      subscription.removeListener("subscribed", handleSubscribed);
      subscription.unsubscribe();
      client.removeListener("error", handleError);
      client.disconnect();

      if (subscriptionRef.current === subscription) {
        subscriptionRef.current = null;
      }

      if (clientRef.current === client) {
        clientRef.current = null;
      }
    };
  }, [dispatch, isNewChatEnabled, userId]);
};

export default useNewChatUnreadListener;
